import React, { useEffect, useState } from 'react'
import { ConnectedProps, connect } from 'react-redux'
import { compose } from 'recompose'

import { Administration, EmergencyDose } from 'services/administrations/models'
import { UPLOAD_DIRECTORY } from 'services/legacy/upload/constants'
import { MF_SIGNATURE_TYPES } from 'services/medicationForms/constants'
import { Signature } from 'services/medicationForms/models'

import { withAdministrationsService, withAdministrationsServiceProps } from 'services/administrations'
import { withAppService, withAppServiceProps } from 'services/app'
import { withUploadService, withUploadServiceProps } from 'services/legacy/upload'
import { withModalService, withModalServiceProps } from 'services/utils/modal'

import { RootState } from 'core/reducers'

import AddAdministrationView from './AdministrationParentSignatureModalView'
import { AdministrationParentSignatureFormValues } from './components/AdministrationParentSignatureModalForm'

const ADMINISTRATION_GROUPS = {
  read: [
    'administration',
    'administration.medicationForm',
    'administration.medicationDetails',
    'administration.contactedParent',
    'administration.child',
    'administration.signatures',
    'administrationSignature',
    'administrationSignature.owner',
    'administrationSignature.signature',
    'medicationForm',
    'medicationForm.medicationDetails',
    'medicationForm:medicationDetails',
    'medicationDetails',
    'medicationForm.child',
    'medium',
    'user',
    'orm',
    'carer',
    'membership',
  ],
}

export interface AdministrationParentSignatureModalProps {
  id: number
  onSubmitSuccess?: () => void
}

export type AdministrationParentSignatureModalContainerProps = AdministrationParentSignatureModalProps
  & withAppServiceProps
  & withModalServiceProps
  & withAdministrationsServiceProps
  & withUploadServiceProps

const mapState = (state: RootState, {
  administrationsSingleState,
  appSelectors,
}: AdministrationParentSignatureModalContainerProps) => ({
  errorMessages: appSelectors.getErrorMessages(administrationsSingleState),
  isFetching: appSelectors.getIsFetching(administrationsSingleState),
})

const connector = connect(mapState)

type PropsFromRedux = ConnectedProps<typeof connector>
type AdministrationParentSignatureModalContainerFinalProps = AdministrationParentSignatureModalContainerProps
  & PropsFromRedux

const AdministrationParentSignatureModalContainer: React.FC<AdministrationParentSignatureModalContainerFinalProps> = ({
  administrationsActions,
  administrationsSelectors,
  errorMessages,
  id,
  isFetching,
  modalActions,
  onSubmitSuccess,
  uploadActions,
}) => {
  const [administration, setAdministration] = useState<Administration & EmergencyDose>()
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  useEffect(() => {
    if (id) {
      administrationsActions.get({
        onSuccess: ({ data }) => setAdministration(data),
        params: [id, {
          groups: ADMINISTRATION_GROUPS,
        }],
      })
    }

    return () => {
      administrationsActions.clear()
    }
  }, [])

  const handleCancelClick = () => {
    modalActions.hide()
  }

  const handleSubmit = async (fields: AdministrationParentSignatureFormValues) => {
    setIsSubmitting(true)

    const { parent, parentSignature } = fields

    const existingSignatureBody = administrationsSelectors.getExistingSignatureBody(administration)

    const signatureBody: Signature[] = []

    signatureBody.push({
      carer: {
        id: parent.value as number,
      },
      signature: {
        mimeType: parentSignature?.file?.type,
        size: parentSignature?.file?.size,
        url: await uploadActions.uploadFile(
          parentSignature?.file,
          parentSignature?.file.name,
          UPLOAD_DIRECTORY.IMAGES,
        ) as any,
      },
      type: MF_SIGNATURE_TYPES.PARENT,
    })

    administrationsActions.update({
      body: {
        signatures: [
          ...existingSignatureBody,
          ...signatureBody,
        ],
      },
      onFailed: () => {
        setIsSubmitting(false)
      },
      onSuccess: ({ data }) => {
        administrationsActions.updateItem(data)
        setIsSubmitting(false)
        handleCancelClick()
        onSubmitSuccess?.()
      },
      params: [id, {
        groups: ADMINISTRATION_GROUPS,
      }],
    })
  }

  return (
    <AddAdministrationView
      administration={administration}
      errorMessages={errorMessages}
      isLoading={isFetching}
      isSubmitting={isSubmitting}
      onCancelClick={handleCancelClick}
      onSubmit={handleSubmit}
    />
  )
}

const enhance = compose(
  withAppService,
  withModalService,
  withAdministrationsService,
  withUploadService,
  connector,
)

export default enhance(AdministrationParentSignatureModalContainer)

